import type { JwtPayload } from "@connectdevice/types";
import type { BatteryStatus } from "@connectdevice/types";

const API_BASE = "/api";

type SessionExpiredListener = () => void;

const sessionExpiredListeners = new Set<SessionExpiredListener>();
let sessionExpiredNotified = false;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

export function resetSessionExpiredNotification(): void {
  sessionExpiredNotified = false;
}

function shouldNotifySessionExpired(url: string, status: number): boolean {
  return status === 401 && !url.startsWith("/auth/");
}

function notifySessionExpired(): void {
  if (sessionExpiredNotified) return;
  sessionExpiredNotified = true;
  for (const listener of sessionExpiredListeners) {
    listener();
  }
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers);
  if (options?.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: "include", // Include HttpOnly cookies
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    const message = (error as { error?: string }).error ?? `HTTP ${response.status}`;
    if (shouldNotifySessionExpired(url, response.status)) {
      notifySessionExpired();
    }
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<T>;
}

// ─── Auth API ─────────────────────────────────────────────────────────────────

export interface LoginResponse {
  user: Pick<JwtPayload, "sub" | "username" | "clientId" | "role">;
}

export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  return fetchJson<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export async function logout(): Promise<void> {
  await fetchJson("/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<LoginResponse> {
  return fetchJson<LoginResponse>("/auth/me");
}

// ─── Battery Tests API ────────────────────────────────────────────────────────

export interface BatteryTestResult {
  id: number;
  serialNumber: string;
  clientId: number;
  equipmentId: string | null;
  measuredMah: number;
  internalResistance: number | null;
  configuredMah: number;
  status: BatteryStatus;
  description: string | null;
  createdAt: string | null;
}

export interface PaginatedTests {
  data: BatteryTestResult[];
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
}

export interface BatteryTestFilters {
  status?: BatteryStatus;
  equipmentId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export async function getBatteryTests(
  page = 1,
  limit = 20,
  filters: BatteryTestFilters = {},
): Promise<PaginatedTests> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      params.set(key, value);
    }
  }

  return fetchJson<PaginatedTests>(`/battery-tests?${params.toString()}`);
}

// ─── Battery Models API ───────────────────────────────────────────────────────

export interface BatteryModel {
  id: number;
  clientId: number;
  name: string;
  voltage: number;
  capacityMah: number;
  approvalThreshold: number;
  rejectionThreshold: number;
  description: string | null;
  createdAt: string | null;
}

export async function getBatteryModels(): Promise<{ data: BatteryModel[] }> {
  return fetchJson<{ data: BatteryModel[] }>("/battery-models");
}

export async function createBatteryModel(
  payload: Omit<BatteryModel, "id" | "clientId" | "createdAt">,
): Promise<{ data: BatteryModel }> {
  return fetchJson<{ data: BatteryModel }>("/battery-models", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
